// oxlint-disable typescript/no-unnecessary-type-parameters
import { useDeepCompareCallback } from 'use-deep-compare';
import { type InteractionEvent } from '../events.types';
import { trackInteraction } from '../tracking';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type RequiredKeysOf<T> = { [K in keyof T]-?: {} extends Pick<T, K> ? never : K }[keyof T];

/**
 * A hook that returns a function to track user interactions with specified properties.
 * It uses deep comparison to avoid unnecessary re-renders when the event payload is an object.
 */
export function useInteractionTracking<
  TEventPayload extends InteractionEvent,
  TEventPayloadInferred,
  T extends Partial<InteractionEvent> & Omit<TEventPayload, keyof TEventPayloadInferred>,
>(eventPayload: Partial<TEventPayload> & Partial<TEventPayloadInferred>) {
  return useDeepCompareCallback(
    function eventTracking(
      ...args: RequiredKeysOf<Omit<TEventPayload, keyof TEventPayloadInferred>> extends never
        ? [actionPayload?: T]
        : [actionPayload: T]
    ) {
      const actionPayload = args[0];
      if (actionPayload instanceof Event) {
        throw new Error('Invalid action payload: "Event" object is not allowed.');
      }
      return trackInteraction(Object.assign({}, eventPayload, actionPayload) as InteractionEvent);
    },
    [eventPayload],
  );
}
